import type { TFunction } from 'i18next'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router'
import { Button, Card, ChangeList, DownloadRow, Icon, ImagePlaceholder, Sheet, Skeleton, StatusChip, StayHeader } from '@/ds'
import type { StatusTone } from '@/ds'
import { dayMonth, dayMonthLong, dayMonthTime, formatMoney, formatMoneyShort, time, weekdayDayMonth } from '@/lib/format'
import { useAppNavigate, withQuery } from '@/lib/query-params'
import { asyncValue, useAsync } from '@/lib/useAsync'
import { featureEnabled } from '@/config/features'
import { acompanantesService } from '@/services/acompanantes'
import type { Invitation } from '@/services/acompanantes'
import { fianzaService } from '@/services/fianza'
import type { Deposit } from '@/services/fianza'
import { pagosService } from '@/services/pagos'
import { peticionesService } from '@/services/peticiones'
import { reservaService } from '@/services/reserva'
import type { BookingPolicy, ChangeOption, ChangeOptions, Stay } from '@/services/reserva'
import { useApiLang, useSave } from '../Checkin/useSave'
import { SectionError } from '../Estancia/sections/SectionError'
import { PAY_ROUTES, plainText } from '../Pagos/shared'
import { BOOKING_ROUTES, BOOKING_SECTIONS, changeQuery, useBookingStay, useLang, writeDraft } from './shared'
import { PendingChangeBanner } from './PendingChangeBanner'
import { RequestTracking } from './RequestTracking'

const CHECKIN_ROUTE = '/checkin'

/** "sáb 3 oct · 16:00" */
const when = (iso: string, lang: string) => `${weekdayDayMonth(iso, lang)} · ${time(iso, lang)}`

/** Enlace con chevron de las tarjetas del bundle ("Pagos, fianza y recibos ›"), conservando la query. */
function ChevronLink({ to, children }: { to: string; children: React.ReactNode }) {
  const { go, search } = useAppNavigate()
  return (
    <a
      href={withQuery(to, search, changeQuery('reserva'))}
      className="pagos__chevron-link"
      onClick={(e) => {
        e.preventDefault()
        go(to, { query: changeQuery('reserva') })
      }}
    >
      {children}
      <Icon name="chevron-right" size={20} />
    </a>
  )
}

function Row({ label, children, chip }: { label: React.ReactNode; children: React.ReactNode; chip?: React.ReactNode }) {
  return (
    <div className="reserva__row">
      <dt>
        {label}
        {chip}
      </dt>
      <dd className="tnum">{children}</dd>
    </div>
  )
}

/** Fianza en el registro de la reserva, con el vocabulario neutro de D7 ("retenida", "liberada"). */
function depositLabel(d: Deposit, t: TFunction): string {
  switch (d.status) {
    case 'pending':
      return t('v2.reserva.precio.fianzaPendiente')
    case 'held':
      return t('v2.reserva.precio.fianzaRetenida')
    case 'review':
      return t('v2.reserva.precio.fianzaRevision')
    default:
      return t('v2.reserva.precio.fianzaLiberada')
  }
}

/** Dos frases con fechas concretas (§5a): cambios y cancelación. Solo lo que da el backend. */
export function policySentences(policy: BookingPolicy, stay: Stay, lang: string, t: TFunction): string[] {
  const out: string[] = []
  if (policy.changeFreeUntil) {
    const hours = Math.round((new Date(stay.dates.checkIn).getTime() - new Date(policy.changeFreeUntil).getTime()) / 3600000)
    out.push(t('v2.reserva.politica.cambios', { count: hours, date: dayMonthTime(policy.changeFreeUntil, lang) }))
  }
  if (policy.cancelFreeUntil) {
    const date = dayMonthLong(policy.cancelFreeUntil, lang)
    if (policy.refundPercentAfter === null) out.push(t('v2.reserva.politica.cancelacion', { date }))
    else if (policy.refundPercentAfter === 0) out.push(t('v2.reserva.politica.cancelacionSinDevolucion', { date }))
    else out.push(t('v2.reserva.politica.cancelacionPorcentaje', { date, percent: policy.refundPercentAfter }))
  }
  return out
}

/** Subtítulo de cada entrada de cambio (§5a): su condición. */
export function conditionFor(o: ChangeOption, all: ChangeOptions, stay: Stay, lang: string, t: TFunction, now: Date): string {
  if (!o.allowed) return o.blockedBy === 'channel' && stay.channel.name ? t('v2.reserva.cambios.bloqueadoCanal', { channel: stay.channel.name }) : t('v2.reserva.cambios.bloqueado')
  const { policy } = all
  const changeFree = policy.changeFreeUntil && now < new Date(policy.changeFreeUntil) ? policy.changeFreeUntil : null
  switch (o.type) {
    case 'dates':
      return changeFree ? t('v2.reserva.cambios.fechasGratis', { date: dayMonth(changeFree, lang) }) : t('v2.reserva.cambios.fechasSub')
    case 'nights':
      return t('v2.reserva.cambios.nochesSub')
    case 'guests':
      return o.maxGuests ? t('v2.reserva.cambios.personasMax', { count: o.maxGuests }) : t('v2.reserva.cambios.personasSub')
    case 'unit':
      return t('v2.reserva.cambios.unidadSub')
    case 'early_checkin':
      return o.time && o.price ? t('v2.reserva.cambios.entradaSub', { time: o.time, price: formatMoneyShort(o.price, lang) }) : t('v2.reserva.cambios.segunHora')
    case 'late_checkout':
      return o.time && o.price ? t('v2.reserva.cambios.salidaSub', { time: o.time, price: formatMoneyShort(o.price, lang) }) : t('v2.reserva.cambios.segunHora')
    case 'cancel': {
      if (policy.cancelFreeUntil && now < new Date(policy.cancelFreeUntil)) return t('v2.reserva.cambios.cancelarGratis', { date: dayMonth(policy.cancelFreeUntil, lang) })
      const datesAllowed = all.options.some((x) => x.type === 'dates' && x.allowed)
      if (policy.refundPercentAfter !== null && policy.refundPercentAfter > 0) return datesAllowed && changeFree ? t('v2.reserva.cambios.cancelarPorcentajeAlternativa', { percent: policy.refundPercentAfter }) : t('v2.reserva.cambios.cancelarPorcentaje', { percent: policy.refundPercentAfter })
      return t('v2.reserva.cambios.cancelarSub')
    }
  }
}

function Occupants({ stay }: { stay: Stay }) {
  const { t, lang } = useLang()
  const { go } = useAppNavigate()
  const showInvitations = featureEnabled('invitados')
  const [invitations, reloadInvitations] = useAsync(`reserva-acompanantes:${stay.guestCode}`, (signal) => (showInvitations ? acompanantesService.list(stay, signal) : Promise.resolve([])))
  const [revoked, reloadRevoked] = useAsync(`reserva-revocados:${stay.guestCode}`, (signal) => (showInvitations ? acompanantesService.listRevoked(stay, signal) : Promise.resolve([])))
  const [confirm, setConfirm] = useState<{ id: string; name: string } | null>(null)
  const { saving, error, run } = useSave()
  const reg = stay.registration
  const revokedIds = asyncValue(revoked) ?? []
  const invited = asyncValue(invitations) ?? []
  // Plazas sin nadie registrado ni invitado: "Persona N · pendiente" (D8).
  const free = Math.max(0, reg.pending - invited.length)
  const firstFree = reg.travelers.length + invited.length + 1

  const revoke = async () => {
    if (!confirm) return
    const ok = await run(async () => {
      await acompanantesService.revoke(stay, confirm.id)
      return true
    })
    if (ok) {
      setConfirm(null)
      reloadInvitations()
      reloadRevoked()
    }
  }

  const rows: { id: string; name: string; sub?: string; tone: StatusTone; chip: string; revokeId?: string; register?: boolean }[] = [
    ...reg.travelers.map((tr) => {
      const name = [tr.name, tr.surname].filter(Boolean).join(' ')
      const isRevoked = showInvitations && revokedIds.includes(tr.id)
      return {
        id: tr.id,
        name,
        ...(tr.canDelete ? {} : { sub: t('v2.reserva.ocupantes.titular') }),
        tone: isRevoked ? ('neutral' as const) : tr.signaturePending ? ('warning' as const) : ('success' as const),
        chip: isRevoked ? t('v2.reserva.ocupantes.sinAcceso') : tr.signaturePending ? t('v2.checkin.ocupantes.faltaFirma') : t('v2.checkin.ocupantes.registrada'),
        ...(showInvitations && tr.canDelete && !isRevoked ? { revokeId: tr.id } : {}),
      }
    }),
    ...(showInvitations ? invited.map((inv: Invitation) => ({
      id: inv.id,
      name: inv.name ?? inv.contact,
      sub: t(`v2.checkin.ocupantes.invitado_${inv.channel}`, { date: dayMonth(inv.sentAt, lang) }),
      tone: 'warning' as const,
      chip: t('v2.checkin.ocupantes.pendiente'),
      revokeId: inv.id,
    })) : []),
    ...Array.from({ length: free }, (_, i) => ({ id: `free-${i}`, name: t('v2.checkin.ocupantes.persona', { n: firstFree + i }), tone: 'warning' as const, chip: t('v2.checkin.ocupantes.pendiente'), register: true })),
  ]

  return (
    <section id={BOOKING_SECTIONS.occupants} className="reserva__section" aria-labelledby="reserva-ocupantes" tabIndex={-1}>
      <h2 id="reserva-ocupantes">{t('v2.reserva.ocupantes.titulo')}</h2>
      <Card flush>
        <ul className="reserva__people">
          {rows.map((r) => (
            <li key={r.id} className="reserva__person">
              <div className="reserva__person-text">
                <span className="reserva__person-name">{r.name}</span>
                {r.sub && <span className="reserva__person-sub">{r.sub}</span>}
                <StatusChip tone={r.tone}>{r.chip}</StatusChip>
              </div>
              {r.register && (
                <Button variant="secondary" fullWidth={false} onClick={() => go(CHECKIN_ROUTE)}>
                  {t('v2.checkin.ocupantes.registrar')}
                </Button>
              )}
              {r.revokeId && (
                <Button variant="tertiary" fullWidth={false} onClick={() => setConfirm({ id: r.revokeId ?? '', name: r.name })} aria-label={`${t('v2.checkin.ocupantes.quitarAcceso')} · ${r.name}`}>
                  {t('v2.checkin.ocupantes.quitarAcceso')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      </Card>
      {showInvitations && <p className="pagos__note">{t('v2.checkin.ocupantes.alcance')}</p>}
      {showInvitations && <Sheet open={confirm !== null} destructive title={t('v2.checkin.ocupantes.quitarTitulo', { name: confirm?.name ?? '' })} confirmLabel={t('v2.checkin.ocupantes.quitarAcceso')} onConfirm={() => void revoke()} onCancel={() => setConfirm(null)} loading={saving}>
        {t('v2.checkin.ocupantes.quitarTexto')}
        {error && (
          <span className="pagos__error" role="alert">
            {error}
          </span>
        )}
      </Sheet>}
    </section>
  )
}

/**
 * 8.5 · Tu reserva (Reserva.dc.html screen=detail, Bloque 5 §5a). Es el registro estable de la reserva: fechas,
 * unidad, precio, política, ocupantes, pagos y recibos, documentos, y las entradas de cambio. Tu estancia enlaza
 * aquí por sección (`/reserva#documentos`) y no repite estos datos.
 */
export function Reserva() {
  const { t, lang } = useLang()
  const apiLang = useApiLang()
  const stay = useBookingStay()
  const { go, search } = useAppNavigate()
  const { hash } = useLocation()
  const [summary, reloadSummary] = useAsync(`reserva-pagos:${stay.guestCode}:${stay.totalPrice?.amount ?? 0}`, (signal) => pagosService.getPaymentSummary(stay, apiLang, signal))
  const [deposit] = useAsync(`reserva-fianza:${stay.guestCode}`, (signal) => fianzaService.getDeposit(stay, apiLang, signal))
  const [options, reloadOptions] = useAsync(`reserva-cambios:${stay.guestCode}:${stay.dates.checkIn}:${stay.dates.checkOut}`, (signal) => reservaService.getChangeOptions(stay, apiLang, signal))
  const [pendingChange] = useAsync(`reserva-cambio-pendiente:${stay.guestCode}:${stay.dates.checkIn}:${stay.dates.checkOut}`, (signal) => reservaService.getPendingChange(stay, apiLang, signal))
  const showRequests = featureEnabled('ayudaPeticiones')
  const [requests] = useAsync(`reserva-peticiones:${stay.guestCode}`, (signal) => (showRequests ? peticionesService.list(stay, signal) : Promise.resolve([])))
  const [conditions, setConditions] = useState(false)
  const [now] = useState(() => new Date())

  // Llegar a una sección desde otra pantalla (`/reserva#documentos`): se desplaza y lleva el foco a ella.
  const ready = options.kind !== 'loading' && summary.kind !== 'loading'
  useEffect(() => {
    if (!hash || !ready) return
    const el = document.getElementById(hash.slice(1))
    el?.scrollIntoView?.({ block: 'start' })
    el?.focus({ preventScroll: true })
  }, [hash, ready])

  const cancelled = stay.phase === 'cancelled'
  const changeable = !cancelled && stay.phase !== 'after'
  const s = asyncValue(summary)
  const d = asyncValue(deposit)
  const o = asyncValue(options)
  const photo = stay.property.coverImageUrl ?? stay.unit.photos[0]
  const policy = o ? policySentences(o.policy, stay, lang, t) : []
  const changeRequests = showRequests ? (asyncValue(requests) ?? []).filter((r) => r.type === 'booking_change') : []

  const openOption = (opt: ChangeOption) => {
    writeDraft({ guestCode: stay.guestCode, type: opt.type, input: null, quote: null, reason: opt.allowed ? null : (opt.blockedBy ?? 'property') })
    if (!opt.allowed) go(BOOKING_ROUTES.unavailable(opt.type))
    else if (opt.type === 'cancel') go(BOOKING_ROUTES.cancel)
    else go(BOOKING_ROUTES.pick(opt.type), { query: changeQuery('reserva') })
  }
  const hrefFor = (opt: ChangeOption) => (!opt.allowed ? withQuery(BOOKING_ROUTES.unavailable(opt.type), search) : opt.type === 'cancel' ? withQuery(BOOKING_ROUTES.cancel, search) : withQuery(BOOKING_ROUTES.pick(opt.type), search, changeQuery('reserva')))

  return (
    <main className="page reserva">
      <StayHeader
        headingLevel={2}
        propertyName={stay.property.name}
        subtitle={stay.unit.name}
        {...(photo ? { image: photo } : {})}
        imageLabel={t('v2.reserva.foto', { name: stay.unit.name })}
        className="reserva__stay-header"
      />
      <div className="pagos__head">
        <h1>{t('v2.reserva.titulo')}</h1>
        <p className="page__lead">{cancelled ? t('v2.reserva.leadCancelada', { name: stay.property.name }) : t('v2.reserva.lead', { name: stay.property.name })}</p>
      </div>
      {asyncValue(pendingChange) && <PendingChangeBanner pending={asyncValue(pendingChange)!} />}

      <section id={BOOKING_SECTIONS.dates} className="reserva__section" aria-label={t('v2.reserva.datos.titulo')} tabIndex={-1}>
        <Card image={photo ?? <ImagePlaceholder label={t('v2.reserva.foto', { name: stay.unit.name })} />} imageAlt={t('v2.reserva.foto', { name: stay.unit.name })}>
          <h2 className="reserva__unit">{stay.unit.name}</h2>
          {cancelled && <StatusChip tone="error">{t('v2.legacy.texts.Reservation_Cancelled')}</StatusChip>}
          <dl className="reserva__rows">
            <Row label={t('v2.reserva.datos.entrada')}>{when(stay.dates.checkIn, lang)}</Row>
            <Row label={t('v2.reserva.datos.salida')}>{when(stay.dates.checkOut, lang)}</Row>
            <Row label={t('v2.reserva.datos.personas')}>{[t('v2.reserva.datos.adultos', { count: stay.guests.adults }), ...(stay.guests.children > 0 ? [t('v2.reserva.datos.ninos', { count: stay.guests.children })] : [])].join(', ')}</Row>
            <Row label={t('v2.reserva.datos.referencia')}>{stay.reference}</Row>
            {stay.channel.name && <Row label={t('v2.reserva.datos.canal')}>{stay.channel.name}</Row>}
          </dl>
        </Card>
      </section>

      <section id={BOOKING_SECTIONS.payments} className="reserva__section" aria-label={t('v2.reserva.precio.seccion')} tabIndex={-1}>
        <Card>
          <dl className="reserva__rows">
            {stay.totalPrice && <Row label={t('v2.reserva.precio.alojamiento', { count: stay.dates.nights })}>{formatMoney(stay.totalPrice, lang)}</Row>}
            {stay.flags.paidWithGateway && summary.kind === 'loading' && <Skeleton height={44} />}
            {s && stay.flags.paidWithGateway && (
              <Row label={t('v2.reserva.precio.pagado')} chip={s.pending.amount > 0 ? <StatusChip tone="warning">{t('v2.reserva.precio.pendiente', { amount: formatMoney(s.pending, lang) })}</StatusChip> : <StatusChip tone="success">{t('v2.reserva.precio.completo')}</StatusChip>}>
                {formatMoney(s.paid, lang)}
              </Row>
            )}
            {d && d.amount.amount > 0 && <Row label={depositLabel(d, t)}>{formatMoney(d.amount, lang)}</Row>}
          </dl>
          {summary.kind === 'error' && <SectionError code={summary.code} onRetry={reloadSummary} />}
          <div className="reserva__links">
            <ChevronLink to={PAY_ROUTES.payments}>{t('v2.reserva.precio.enlace')}</ChevronLink>
            {d && <ChevronLink to={PAY_ROUTES.deposit}>{t('v2.reserva.precio.fianzaEnlace')}</ChevronLink>}
            <ChevronLink to={PAY_ROUTES.receipts}>{t('v2.pagos.enlaces.recibos')}</ChevronLink>
          </div>
        </Card>
      </section>

      {(policy.length > 0 || o?.policy.conditions) && (
        <section id={BOOKING_SECTIONS.policy} className="reserva__policy" aria-labelledby="reserva-politica" tabIndex={-1}>
          <h2 id="reserva-politica" className="reserva__policy-title">
            {t('v2.reserva.politica.titulo')}
          </h2>
          {policy.length > 0 && <p className="reserva__policy-text">{policy.join(' ')}</p>}
          {o?.policy.conditions && (
            <button type="button" className="pagos__link-button" onClick={() => setConditions(true)}>
              {t('v2.reserva.politica.condiciones')}
            </button>
          )}
        </section>
      )}

      {changeable && (
        <section id={BOOKING_SECTIONS.changes} className="reserva__section" aria-labelledby="reserva-cambios" tabIndex={-1}>
          <h2 id="reserva-cambios">{t('v2.reserva.cambios.titulo')}</h2>
          {options.kind === 'loading' ? (
            <div aria-busy="true">
              <Skeleton height={64 * 3} />
            </div>
          ) : options.kind === 'error' ? (
            <SectionError code={options.code} onRetry={reloadOptions} />
          ) : o ? (
            <ChangeList
              items={o.options.map((opt) => ({
                id: opt.type,
                title: t(`v2.reserva.cambios.${opt.type}`),
                condition: conditionFor(opt, o, stay, lang, t, now),
                destructive: opt.type === 'cancel',
                href: hrefFor(opt),
                onClick: () => openOption(opt),
              }))}
              className="reserva__changes"
            />
          ) : null}
        </section>
      )}

      <Occupants stay={stay} />

      {stay.documents.length > 0 && (
        <section id={BOOKING_SECTIONS.documents} className="reserva__section" aria-labelledby="reserva-documentos" tabIndex={-1}>
          <h2 id="reserva-documentos">{t('v2.reserva.documentos.titulo')}</h2>
          <Card flush>
            {stay.documents.map((doc) => (
              <DownloadRow key={doc.url} title={doc.name} meta={doc.type} href={doc.url} />
            ))}
          </Card>
        </section>
      )}

      {changeRequests.length > 0 && (
        <section className="reserva__section" aria-labelledby="reserva-peticiones">
          <h2 id="reserva-peticiones">{t('v2.reserva.peticiones.titulo')}</h2>
          {changeRequests.map((r) => (
            <RequestTracking key={r.id} request={r} compact />
          ))}
        </section>
      )}

      <Sheet open={conditions} title={t('v2.legacy.payment.Conditions')} confirmHidden onCancel={() => setConditions(false)}>
        <span className="pagos__pre">{plainText(o?.policy.conditions ?? '')}</span>
      </Sheet>
    </main>
  )
}

